import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {vulnerabilityData} from "../../../common/data/demo/vulnerabilityscan/scanData";
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
} from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";

// Redux
import { useSelector, useDispatch } from "react-redux";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";

const ScanHistory = () => {
  document.title = "Tasks";


  const [taskData, setTaskData] = useState([]); // State to store fetched task data
  const [isLoading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null); // For delete modal
  const [viewModal, setViewModal] = useState(false); // For view task modal
  const [taskDetails, setTaskDetails] = useState(null); // To hold the selected task details
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username; // Replace with your user ID

  const EmptyState = () => (
    <div className="text-center p-5">
      <div className="mb-4">
        <i className="mdi mdi-file-search text-primary" style={{ fontSize: "4rem" }} />
      </div>
      <h3 className="mb-3 font-weight-bold">No Scans Found</h3>
      <p className="text-muted mb-4">
        You haven't performed any Scan yet. Start a Scan to detect potential vulnerabilities.
      </p>
      <div className="d-flex justify-content-center">
        <Link to="/vulnerabilityScan" className="btn btn-primary px-4 py-2">
          Start New Scan
        </Link>
      </div>
    </div>
  );

  // Fetching task data from the API
  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        const response = await fetch(`https://api.compliance.cx/api/vulnerability-scan/${userId}`); // Replace with your API endpoint
        const data = await response.json();
        console.log(data)
        setTaskData(data.reverse()); // Set fetched task data
        setLoading(false); // Stop loading
      } catch (error) {
        if (userId === "demo") {
          setTaskData(vulnerabilityData.reverse());
        }
        console.error("Error fetching task data:", error);
        setLoading(false);
      }
    };
    fetchTaskData();
  }, []);

  // Handle delete action
  const onClickDelete =async (scanId) => {
    try {
      // Prepare the request body with "Malware Scan" as the category
      const requestBody = {
        scanId: scanId,  // Use scanId
        category: "Vulnerability Scan",  // Set category to "Malware Scan"
      };
      console.log("Request body:", requestBody);
      // Send the DELETE request to the API with the JSON body
      const response = await fetch(`https://api.compliance.cx/trash/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json', // Set the content type
        },
        body: JSON.stringify(requestBody), // Send the JSON payload
      });
      console.log("Response:", response.text());
      if (response.ok) {
        // If deletion is successful, remove the task from the list in state
        setTaskData(prevTasks => prevTasks.filter(task => task.scanId !== scanId));
        console.log('Task deleted successfully');
      } else {
        console.error('Error deleting task:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  

  // Table Columns
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "scanId", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      
      {
        header: "Asset Name",
        accessorKey: "Basic.Name", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Description",
        accessorKey: "Basic.Description", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Initiated",
        accessorKey: "timestamp", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Last Scan",
        accessorKey: "timestamp", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Badge
            className={
              cellProps.row.original.status === "New" 
                ? "bg-info"
                : cellProps.row.original.status === "Analyzed"
                ? "bg-success"
                : cellProps.row.original.status === "Unapproved"
                ? "bg-danger"
                : "bg-secondary"
            }
          >
            {cellProps.row.original.status}
          </Badge>
        ),
      },
      {
        header: "Resolved",
        accessorKey: "resolved",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const { resolved, scanId } = cellProps.row.original;
      
          // If resolved is null or undefined, show placeholder "-"
          if (resolved === null || resolved === undefined) {
            return <span>-</span>;
          }
      
          // Function to toggle resolved status
          const toggleResolvedStatus = () => {
            const newResolvedStatus = !resolved;
      
            // Optimistic update: Update the task data in the state
            setTaskData((prevTaskData) =>
              prevTaskData.map((task) =>
                task.scanId === scanId ? { ...task, resolved: newResolvedStatus } : task
              )
            );
      
            // Send the backend update request
            fetch(`https://api.compliance.cx/api/vulnerability-scan/${userId}/results/${scanId}`, {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ scanId, resolved: newResolvedStatus }),
            })
              .then((response) => {
                if (!response.ok) {
                  console.error("Failed to update resolved status:", response.statusText);
      
                  // Revert state in case of failure (optimistic update reversal)
                  setTaskData((prevTaskData) =>
                    prevTaskData.map((task) =>
                      task.scanId === scanId ? { ...task, resolved } : task
                    )
                  );
                }
              })
              .catch((error) => {
                console.error("Error updating resolved status:", error);
      
                // Revert state in case of error
                setTaskData((prevTaskData) =>
                  prevTaskData.map((task) =>
                    task.scanId === scanId ? { ...task, resolved } : task
                  )
                );
              });
          };
      
          return (
            <Button
              color={resolved ? "success" : "danger"}
              size="sm"
              onClick={toggleResolvedStatus}
              disabled={resolved === null || resolved === undefined}
            >
              {resolved ? "Resolved" : "Unresolved"}
            </Button>
          );
        },
      },      
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return (
            <div className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Link
                  to={`/scanresults/${cellProps.row.original.scanId}`}
                  className="btn btn-sm btn-soft-primary"
                  id={`viewtooltip${cellProps.row.original.scanId}`}
                >
                  <i className="mdi mdi-eye-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`viewtooltip${cellProps.row.original.scanId}`}
                  >
                    View
                  </UncontrolledTooltip>
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => {
                    const taskData = cellProps.row.original;
                    onClickDelete(taskData.scanId);
                  }}
                  id={`deletetooltip${cellProps.row.original.scanId}`}
                >
                  <i className="mdi mdi-delete-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`deletetooltip${cellProps.row.original.scanId}`}
                  >
                    Delete
                  </UncontrolledTooltip>
                </Link>
              </li>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      
      
      {/* View Modal with adjusted size */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(!viewModal)}
        size="lg"
        style={{ maxWidth: "80%", width: "80%", height: "80%", margin: "auto" }} // Adjusted to take up 80% of the screen
        centered
      >
        <ModalHeader toggle={() => setViewModal(false)}>
          {taskDetails ? taskDetails.scanId : "Loading..."} {/* Displaying Task ID */}
        </ModalHeader>
        <ModalBody style={{ height: "70vh", overflowY: "auto" }}>
  {taskDetails ? (
    <div>
      <h5>{taskDetails.assetName}</h5>
      <p><strong></strong> {taskDetails.description}</p>
      <p><strong>Initiated:</strong> {taskDetails.initiated}</p>
      <p><strong>Last Scan:</strong> {taskDetails.lastScan}</p>

      {/* Display analysis or show a message if not available */}
      <div>
        <strong>Analysis:</strong> 
        {taskDetails.analysis && taskDetails.analysis.trim() !== "" ? (
          <p>{taskDetails.analysis}</p>
        ) : (
          <p className="text-warning">Analysis is not complete yet.</p>
        )}
      </div>
    </div>
  ) : (
    <div>Loading...</div>
  )}
</ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs breadcrumbItem="Tasks" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {isLoading ? (
                    <div className="text-center p-4">
                      <Spinners setLoading={setLoading} />
                    </div>
                  ) : taskData.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={taskData}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search tasks..."
                      tableClass="table-bordered align-middle nowrap mt-2"
                      pagination="pagination pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withRouter(ScanHistory);
